/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { Formik, Form, Field } from "formik";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import Header from "../../components/Header";
import UploadButton from "../../components/UploadButton";
import BackButton from "../../components/BackButton";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";

import {
  getHtpById,
  updateHtp,
  uploadHtpMedia,
  getHtpCategories,
  HtpCategory,
} from "../../services/howToPerformService";

interface Video {
  url: string;
  thumbnail?: string;
}

interface FormValues {
  name: string;
  description: string;
  categoriesIds: string[];
  imageUrls: (string | null)[];
  videoUrls: (Video | null)[];
}

const EditHowToPerform: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const htp = location.state;

  const [categories, setCategories] = useState<HtpCategory[]>([]);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<any>(null);
  const [uploadedImageNames, setUploadedImageNames] = useState<
    (string | null)[]
  >([null, null, null]);

  /* ================= FETCH ================= */
  useEffect(() => {
    getHtpCategories().then((cats) =>
      setCategories(Array.isArray(cats) ? cats : [cats])
    );
  }, []);

  useEffect(() => {
    if (htp?.id) {
      getHtpById(htp.id).then(setDetails);
    }
  }, [htp?.id]);

  const initialValues: FormValues = {
    name: details?.name || "",
    description: details?.description || "",
    categoriesIds: details?.categories?.map((c: any) => c.id.toString()) || [],
    imageUrls: details?.imageUrls || [],
    videoUrls:
      details?.videoUrls?.map((video: any) => ({
        url: video.url,
        thumbnail: video.thumbnail,
      })) || [],
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (values: FormValues) => {
    console.log(values);
    try {
      await updateHtp(htp.id, {
        name: values.name,
        description: values.description,
        categoriesIds: values.categoriesIds.map(Number),
        imageUrls: values.imageUrls.filter((url): url is string => !!url),
        videoUrls: values.videoUrls
          .filter((video): video is Video => !!video && !!video.url)
          .map((video) => ({
            url: video.url,
            thumbnail: video.thumbnail,
          })),
      });

      setSuccess("Tutorials updated successfully");
      setTimeout(() => navigate("/htp"), 1500);
    } catch (err: any) {
      console.log(err.response.data.message);
      const error =
        err && err.response
          ? err.response.data.message.includes("duplicate")
            ? "Name must be unique"
            : err.response.data.message
          : "Failed to update Tutorials";
      setError(error);
    }
  };

  console.log(error);

  /* ================= MEDIA ================= */
  const handleUpload = async (
    file: File,
    index: number,
    type: "image" | "video",
    setFieldValue: (field: string, value: any) => void,
    values: FormValues
  ): Promise<string | null> => {
    try {
      const res = await uploadHtpMedia(htp.id, file);

      if (type === "image") {
        const oldImage = values.imageUrls?.[0] ?? null;

        const newImages = [res.url, ...(oldImage ? [oldImage] : [])];

        setFieldValue("imageUrls", newImages);
        return res.url;
      } else {
        /* ===== VIDEO ===== */
        const oldVideo = values.videoUrls?.[0] ?? null;

        const newVideos = [
          {
            url: res.url,
            thumbnail: res.thumbnail,
          },
          ...(oldVideo ? [oldVideo] : []),
        ];

        setFieldValue("videoUrls", newVideos);
        return res.url;
      }
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const handleImageRemove = (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    values: FormValues
  ) => {
    const newImages = [...values.imageUrls];
    newImages[index] = null;
    setFieldValue("images", newImages);

    const newImageNames = [...uploadedImageNames];
    newImageNames[index] = null;
    setUploadedImageNames(newImageNames);
  };

  const handleVideoRemove = (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    values: FormValues
  ) => {
    const videos = [...values.videoUrls];

    if (index === 0) {
      if (videos.length > 1) {
        setFieldValue("videoUrls", [videos[1]]);
      } else {
        setFieldValue("videoUrls", []);
      }
      return;
    }

    if (index === 1) {
      setFieldValue("videoUrls", [videos[0]]);
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Edit Trainer Tutorials" />

      {success && (
        <SuccessMessage message={success} onClose={() => setSuccess(null)} />
      )}
      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}

      <BackButton onClick={() => navigate("/htp")} />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className="mt-6 bg-white p-6 rounded shadow border">
            {/* NAME */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Name</label>
              <Field name="name" className="mt-1 w-full border rounded p-2" />
            </div>

            {/* DESCRIPTION */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Description</label>
              <ReactQuill
                value={values.description}
                onChange={(v) => setFieldValue("description", v)}
              />
            </div>

            {/* CATEGORIES */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Categories</label>
              <select
                className="w-full border rounded p-2"
                onChange={(e) =>
                  !values.categoriesIds.includes(e.target.value) &&
                  setFieldValue("categoriesIds", [
                    ...values.categoriesIds,
                    e.target.value,
                  ])
                }
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <div className="flex gap-2 mt-2 flex-wrap">
                {values.categoriesIds.map((id) => {
                  const cat = categories.find((c) => c.id.toString() === id);
                  return (
                    <span key={id} className="bg-gray-100 px-2 py-1 rounded">
                      {cat?.name}
                      <button
                        className="ml-2 text-red-500"
                        onClick={() =>
                          setFieldValue(
                            "categoriesIds",
                            values.categoriesIds.filter((c) => c !== id)
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* MEDIA */}
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <UploadButton
                  index={0}
                  onRemove={(index) =>
                    handleImageRemove(index, setFieldValue, values)
                  }
                  label="Upload Image"
                  accept="image/*"
                  onUpload={async (file, index) =>
                    await handleUpload(
                      file!,
                      index,
                      "image",
                      setFieldValue,
                      values
                    )
                  }
                />
                {initialValues?.imageUrls?.[0] && (
                  <img
                    src={initialValues.imageUrls[0]}
                    alt="Preview"
                    className="mt-2 w-24 h-24 object-cover border rounded"
                  />
                )}
              </div>
              <div className="flex flex-col items-center">
                <UploadButton
                  index={1}
                  onRemove={(index) =>
                    handleVideoRemove(index, setFieldValue, values)
                  }
                  label="Upload Video"
                  accept="video/*"
                  onUpload={async (file, index) =>
                    await handleUpload(
                      file!,
                      index,
                      "video",
                      setFieldValue,
                      values
                    )
                  }
                />
                {initialValues?.videoUrls?.[0] &&
                  initialValues?.videoUrls?.[0].thumbnail && (
                    <img
                      src={initialValues?.videoUrls?.[0].thumbnail}
                      alt="Preview"
                      className="mt-2 w-24 h-24 object-cover border rounded"
                    />
                  )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate("/how-to-perform")}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-yellow-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditHowToPerform;
